import React, { createContext, useContext, useState } from 'react';
import UnifiedFeedback from '../components/common/UnifiedFeedback';
import { getFriendlyErrorMessage } from '../utils/friendlyError';

const FeedbackContext = createContext();

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const [feedback, setFeedback] = useState({
    isVisible: false,
    type: 'loading', // 'loading', 'success', 'error'
    title: '',
    message: '',
    autoClose: true,
    autoCloseDelay: 3000
  });

  // Show loading feedback
  const showLoading = (title = 'Processing...', message = 'Please wait while we process your request.') => {
    setFeedback({
      isVisible: true,
      type: 'loading',
      title,
      message,
      autoClose: false,
      autoCloseDelay: 0
    });
  };

  // Show success feedback
  const showSuccess = (title = 'Success!', message = 'Your request has been completed successfully.', autoCloseDelay = 3000) => {
    setFeedback({
      isVisible: true,
      type: 'success',
      title,
      message,
      autoClose: true,
      autoCloseDelay
    });
  };

  // Show error feedback
  const showError = (title = 'Error', message = 'Something went wrong. Please try again.', autoCloseDelay = 4000) => {
    setFeedback({
      isVisible: true,
      type: 'error',
      title,
      message,
      autoClose: true,
      autoCloseDelay
    });
  };

  // Hide feedback
  const hideFeedback = () => {
    setFeedback(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  // Utility function for handling async operations with feedback
  const handleAsyncOperation = async (
    operation,
    loadingConfig = {},
    successConfig = {},
    errorConfig = {}
  ) => {
    try {
      // Show loading
      showLoading(
        loadingConfig.title || 'Processing...',
        loadingConfig.message || 'Please wait while we process your request.'
      );

      // Execute the operation
      const result = await operation();

      // Show success
      showSuccess(
        successConfig.title || 'Success!',
        successConfig.message || 'Operation completed successfully.',
        successConfig.autoCloseDelay
      );

      return result;
    } catch (error) {
      // Show error — sanitize the error message so raw technical
      // details never leak to the user
      showError(
        errorConfig.title || 'Error',
        errorConfig.message || getFriendlyErrorMessage(error),
        errorConfig.autoCloseDelay
      );

      throw error; // Re-throw so calling code can handle if needed
    }
  };

  const value = {
    feedback,
    showLoading,
    showSuccess,
    showError,
    hideFeedback,
    handleAsyncOperation
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <UnifiedFeedback
        isVisible={feedback.isVisible}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={hideFeedback}
        autoClose={feedback.autoClose}
        autoCloseDelay={feedback.autoCloseDelay}
      />
    </FeedbackContext.Provider>
  );
};

export default FeedbackContext;
